/**
 * Manifeste des six sections de la vitrine `/` — source unique du markup (mission-03) et du
 * moteur d'animation (mission-02).
 *
 * Aucun import ici (y compris de type) : c'est cette clôture qui permet à `mission-02` et
 * `mission-03` de consommer ce fichier sans créer de cycle (plan §6.7a).
 */

/** Identifiant de section, dans l'ordre du document (maquette `prototype-landing-v3.html`). */
export type SceneId = "cockpit" | "coulisses" | "risques" | "assistant" | "carte" | "final";

/**
 * Spécification du rail de transition d'arrivée d'une scène épinglée.
 *
 * `d` : longueur de scrub du rail, en `vh`. `pg` : progression `p` à laquelle la transition
 * d'arrivée est finie (`maquette:4926-4932`).
 */
export type RailSpec = { readonly d: number; readonly pg: number };

/**
 * Démonstration réduite à la largeur de la colonne en mode mobile.
 * `target` : sélecteur relatif à `.pin-outer#<id>`, élément réduit (transform: scale) et observé.
 * `widthPx` : largeur de **dessin** mobile, en px de conception : plus petite largeur ≥ la largeur visible à 1440×813
 *   dont le contenu tient dans `pane` au ratio 16/10 (plan-ecrans §4 bis point 2 ; règle de recalcul : largeur ≥
 *   hauteur du contenu × 1,6). `--demo-s = colonne / widthPx` (`engine/demo-fit.ts`).
 * `pane` : sélecteur de la fenêtre du produit, relatif à `.pin-outer#<id>` (la cible elle-même ou un descendant) ;
 *   sa hauteur mobile vaut `widthPx / 1.6` (règle D1c de `styles/mobile.ts`, `description.md:41`).
 */
export type DemoSpec = { readonly target: string; readonly widthPx: number; readonly pane: string };

/**
 * Écrans d'un cadrage multi-écrans (`CadrageSpec.frame`, plan-figures §5.B point 1) : un écran par porteur.
 * `holder` : sélecteur des porteurs, relatif à `.pin-outer#<id>` ; chaque porteur est le bloc conteneur de sa boîte
 * collante, reçoit les variables `--cad-off`/`--cad-h` et la réserve du palier (`::after`, K1 de `styles/mobile.ts`).
 * `box` : sélecteur **simple** (une classe, sans combinateur) de la boîte collante, **enfant direct** de chaque porteur
 * (résolu par `:scope > <box>`, K2).
 */
export type CadrageFrame = { readonly holder: string; readonly box: string };

/**
 * Cadrage mobile d'une scène : arrêt par palier sur un écran qui tient dans le petit viewport
 * (`description.md:19-30`).
 * - `frame` absent : un seul écran ; porteur `.pin`, boîte collante `.pin > .in` ; sélecteurs relatifs à
 *   `.pin-outer#<id>`, descendants de `.pin > .in` (cockpit).
 * - `frame` présent : un écran par `frame.holder` ; sélecteurs relatifs à **chaque** porteur (coulisses, figures 02-04).
 * `figure` : premier élément de l'écran, posé juste sous l'en-tête ; avec textes, c'est la cible de la démonstration
 * (démo + légende : cockpit, risques, assistant, carte).
 * `texts` présent, modèle « synthèse » : sa marge haute porte la première moitié de la respiration, et celle de
 * `synthesis` la seconde (règle K3 de `styles/mobile.ts`, jamais le markup) : les textes sont centrés entre la légende
 * et la synthèse. `synthesis` est écrite à la taille des titres de scène (K4). K5 translate la synthèse de la hauteur
 * libérée par la barre du navigateur (K5s) et les textes de la moitié (K5t) : tout élément placé entre eux fausserait
 * la symétrie et resterait hors des translations, d'où l'adjacence requise.
 * `texts` absent, modèle « figure centrée » (figures 02-04) : `synthesis` est le bloc du bas (légende + texte) ; la
 * respiration est partagée à parts égales au-dessus de la figure (K2) et entre la figure et le bloc (K3) : la figure
 * est centrée entre l'en-tête et la légende (`description.md:30`) ; sa taille est inchangée (ni K4 ni K5t) ; le bloc
 * suit la barre (K5s), la figure de la moitié (K5f).
 * Ordre visuel requis : figure → [textes →] synthèse ; textes et synthèse adjacents.
 */
export type CadrageSpec = {
  readonly frame?: CadrageFrame;
  readonly figure: string;
  readonly texts?: string;
  readonly synthesis: string;
};

/**
 * Section épinglée (`.pin-outer`) — toutes les sections sauf `final`.
 * `demo` absent = scène sans démonstration (coulisses : figures fixes).
 * `cadrage` absent = pas de palier (défilement normal).
 */
export type PinnedSection = {
  readonly id: Exclude<SceneId, "final">;
  readonly kind: "pin";
  readonly vh: number;
  readonly rail: RailSpec | null;
  readonly eager: boolean;
  readonly demo?: DemoSpec;
  readonly cadrage?: CadrageSpec;
};

/** Section en flux — uniquement `final` : pas de hauteur d'auteur, hauteur de son contenu. */
export type FlowSection = { readonly id: "final"; readonly kind: "flow" };

/** Union discriminée par `kind` des deux formes de section de la vitrine. */
export type Section = PinnedSection | FlowSection;

/**
 * Les six sections de la vitrine, dans l'ordre du document : contrat pour `landing.tsx`.
 *
 * `cockpit` est la seule scène `eager` : son `<h1>` doit être présent au premier rendu
 * (`maquette:1919-1920`). `final` n'a pas de hauteur d'auteur en Phase 1 (posée hors phase par
 * le script `maquette:5124-5352`).
 */
export const SECTIONS: readonly Section[] = [
  { id: "cockpit", kind: "pin", vh: 640, rail: { d: 80, pg: 0.15 }, eager: true, demo: { target: "#cframe", widthPx: 940, pane: "#cframe" }, cadrage: { figure: "#cframe", texts: ".steps", synthesis: "h2.sec" } },
  { id: "coulisses", kind: "pin", vh: 400, rail: { d: 80, pg: 0.216 }, eager: false, cadrage: { frame: { holder: ".figslot", box: ".figbox" }, figure: ".figfig", synthesis: ".figtxt" } },
  { id: "risques", kind: "pin", vh: 760, rail: null, eager: false, demo: { target: "#rq-stage", widthPx: 880, pane: "#rq-frame" }, cadrage: { figure: "#rq-stage", texts: ".steps", synthesis: "h2.sec" } },
  { id: "assistant", kind: "pin", vh: 560, rail: null, eager: false, demo: { target: ".th-visual", widthPx: 880, pane: ".th-frame" }, cadrage: { figure: ".th-visual", texts: ".steps", synthesis: "h2.sec" } },
  { id: "carte", kind: "pin", vh: 760, rail: null, eager: false, demo: { target: "#ce-stage", widthPx: 846, pane: ".ce-frame" }, cadrage: { figure: "#ce-stage", texts: ".steps", synthesis: "h2.sec" } },
  { id: "final", kind: "flow" },
];

/**
 * La section `id` suit-elle immédiatement une section à rail ?
 *
 * Une section à rail mesure `(100 + d)vh` (`engine/scene-engine.ts:105`) et porte deux points de
 * repos de scroll-snap : son haut (`styles/snap.css:6`) et sa sentinelle de fin
 * (`styles/snap.css:12`). Entre les deux, le navigateur arbitre « terminer ou rembobiner » la
 * transition. Les sections concernées par ce prédicat sont donc celles dont le montage tomberait
 * au milieu de cette fenêtre d'arbitrage — c'est le seul critère qui justifie de retarder leur
 * commit React (voir `scene-slot.tsx`).
 *
 * Faux pour la première section (aucune précédente) et pour toute section dont la précédente est
 * en flux ou sans rail.
 */
export function followsRail(id: SceneId): boolean {
  const i = SECTIONS.findIndex((s) => s.id === id);
  if (i <= 0) return false;
  const prev = SECTIONS[i - 1];
  return prev.kind === "pin" && prev.rail !== null;
}
